import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BarChart3, DollarSign, ShoppingBag, Percent, AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPromoReportPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (user && user.role !== 'admin') navigate('/'); }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/promo-codes/${encodeURIComponent(code)}/report`, { withCredentials: true })
      .then((r) => setReport(r.data))
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, [user, code]);

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-12"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!report) return <div className="max-w-5xl mx-auto px-4 py-12 text-muted-foreground">Report not available.</div>;

  const { meta, metrics, orders } = report;
  const m = meta;
  const isAggregate = m.kind === 'aggregate';

  const stats = [
    { label: 'Times Used', value: metrics.total_uses_attempted, icon: BarChart3 },
    { label: 'Completed Orders', value: metrics.completed_orders, icon: ShoppingBag },
    { label: 'Conversion Rate', value: `${metrics.conversion_percent}%`, icon: Percent },
    { label: 'Avg Savings / Order', value: `$${metrics.average_savings_per_order.toFixed(2)}`, icon: DollarSign },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-promo-report-page">
      <Button variant="ghost" size="sm" asChild className="mb-4 -ml-3"><Link to="/admin/promo-codes"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Promo Codes</Link></Button>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[hsl(210,45%,40%)] font-body">Promo Report</p>
          <h1 className="text-3xl font-light font-heading text-foreground mt-1 font-mono">{m.code}</h1>
          {m.description && <p className="text-sm text-muted-foreground mt-1 font-body">{m.description}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary">{m.discount_percent}% off</Badge>
          {!isAggregate && (m.active ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge> : <Badge variant="outline">Inactive</Badge>)}
          {isAggregate && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Auto-generated aggregate</Badge>}
          {m.auto_generated && !isAggregate && <Badge variant="outline">Auto-generated</Badge>}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-6">
        {stats.map((s) => (
          <Card key={s.label} data-testid={`promo-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground"><s.icon className="h-3.5 w-3.5" />{s.label}</div>
              <p className="font-heading text-3xl font-light text-foreground mt-2 tabular-nums">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Cost callout */}
      <Card className="mb-6 border-rose-200 bg-rose-50/40" data-testid="promo-cost-callout">
        <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-rose-700 font-body">Total Cost (Discount Given)</p>
            <p className="font-heading text-3xl font-light text-rose-900 mt-1 tabular-nums">${metrics.total_savings_cost.toFixed(2)}</p>
          </div>
          {metrics.abandoned_sessions > 0 && (
            <div className="text-xs text-amber-800 inline-flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              {metrics.abandoned_sessions} {metrics.abandoned_sessions === 1 ? 'cart' : 'carts'} applied this code without checking out
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aggregate child code list */}
      {isAggregate && m.child_codes && m.child_codes.length > 0 && (
        <Card className="mb-6">
          <CardHeader><CardTitle className="font-heading text-base">Individual Codes ({m.count_codes_issued})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr><th className="px-4 py-2 text-left font-heading">Code</th><th className="px-4 py-2 text-left font-heading">Recipient</th><th className="px-4 py-2 text-left font-heading">Used</th><th className="px-4 py-2 text-left font-heading">Created</th><th className="px-4 py-2 text-left font-heading">Expires</th></tr>
                </thead>
                <tbody>
                  {m.child_codes.map((c) => (
                    <tr key={c.code} className="border-t border-border" data-testid={`child-code-${c.code}`}>
                      <td className="px-4 py-2 font-mono">{c.code}</td>
                      <td className="px-4 py-2 text-muted-foreground">{c.user_email || '—'}</td>
                      <td className="px-4 py-2">{c.times_used > 0 ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Redeemed</Badge> : <span className="text-muted-foreground">Pending</span>}</td>
                      <td className="px-4 py-2 text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders table */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Orders that redeemed this code ({orders.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {orders.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground italic">No orders have completed checkout with this code yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr><th className="px-4 py-2 text-left font-heading">Order</th><th className="px-4 py-2 text-left font-heading">Customer</th><th className="px-4 py-2 text-left font-heading">Method</th><th className="px-4 py-2 text-left font-heading">Code Used</th><th className="px-4 py-2 text-right font-heading">Discount</th><th className="px-4 py-2 text-right font-heading">Total Paid</th><th className="px-4 py-2 text-left font-heading">Date</th></tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-border" data-testid={`promo-order-${o.id}`}>
                      <td className="px-4 py-2 font-mono"><Link to={`/admin/orders?focus=${o.id}`} className="text-[hsl(210,45%,40%)] hover:underline inline-flex items-center gap-1">#{o.id} <ExternalLink className="h-3 w-3" /></Link></td>
                      <td className="px-4 py-2 text-muted-foreground">{o.email}</td>
                      <td className="px-4 py-2 text-xs">{o.payment_method}</td>
                      <td className="px-4 py-2 font-mono text-xs">{o.promo_code}</td>
                      <td className="px-4 py-2 text-right text-rose-700 tabular-nums">−${(o.discount || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-medium tabular-nums">${(o.total || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
